import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Form, Button, Modal, Card, Row, Col, Alert, Badge } from 'react-bootstrap';
// Asegúrate que la ruta a tu _ChatPanel.scss sea correcta si la importas directamente aquí,
// o si la importas en un archivo SCSS global, puedes comentar/eliminar la siguiente línea.
import '../../assets/scss/bandeja-mensajes/ChatPanel.scss';
import EmojiPicker from 'emoji-picker-react';
import axios from '../../auth/authInterceptor';

const API_BASE_URL = '/conversations';
const UPLOADS_HOST = '/uploads';

// 🚀 CACHE GLOBAL PARA CONVERSACIONES - REDUCIDO PARA MAYOR VELOCIDAD
const conversationCache = new Map();
const CACHE_DURATION = 12000; // 12 segundos para conversaciones (reducido de 30s)
let lastConversationFetch = 0;

// 🚀 OPTIMIZACIÓN DE REQUESTS
let globalRequestCanceller = null;

axios.interceptors.response.use(null, (error) => {
  const { config } = error;
  if (axios.isCancel(error) || !config) return Promise.reject(error);
  config.retryCount = config.retryCount || 0;
  const shouldRetry = (
    config.retry && config.retryCount < config.retry &&
    (error.code === 'ECONNABORTED' || error.code === 'ECONNREFUSED' || (error.response && error.response.status >= 500))
  );
  if (shouldRetry) {
    config.retryCount += 1;
    const delay = config.retryDelay || 1000;
    const backoff = Math.pow(2, config.retryCount) * delay;
    return new Promise(resolve => setTimeout(() => resolve(axios(config)), backoff));
  }
  return Promise.reject(error);
});

const formatDate = (ts) => {
  if (!ts) return 'Sin fecha';
  const d = new Date(ts);
  return d.toLocaleString('es-PE', {
    weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

const formatTimeOnly = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleTimeString('es-PE', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false // Formato 24 horas para ser más limpio
  });
};

// 📅 NUEVA FUNCIÓN PARA FORMATEAR FECHA COMPLETA
const formatDateOnly = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Normalizar fechas para comparación (solo día, mes, año)
  const messageDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterdayDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

  if (messageDate.getTime() === todayDate.getTime()) {
    return 'Hoy';
  } else if (messageDate.getTime() === yesterdayDate.getTime()) {
    return 'Ayer';
  } else {
    return d.toLocaleDateString('es-PE', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  }
};

// 📅 FUNCIÓN PARA VERIFICAR SI DOS MENSAJES SON DEL MISMO DÍA
const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();
};

// 🚀 FUNCIÓN HELPER PARA ORDENAR MENSAJES CONSISTENTEMENTE
const sortMessages = (messages) => {
  return [...messages].sort((a, b) => {
    const timestampA = new Date(a.timestamp || a.hora).getTime();
    const timestampB = new Date(b.timestamp || b.hora).getTime();

    // Primero ordenar por timestamp
    if (timestampA !== timestampB) {
      return timestampA - timestampB;
    }

    // Si los timestamps son iguales, ordenar por ID como fallback
    const idA = parseInt(a.id) || 0;
    const idB = parseInt(b.id) || 0;
    return idA - idB;
  });
};

// 📅 COMPONENTE SEPARADOR DE FECHA
const DateSeparator = React.memo(({ date }) => {
  const formattedDate = formatDateOnly(date);

  return (
    <div className="date-separator">
      <div className="date-separator-line"></div>
      <div className="date-separator-text">
        <span>{formattedDate}</span>
      </div>
      <div className="date-separator-line"></div>
    </div>
  );
});

const extractUploadFilename = (text = '') => {
  // **ACTUALIZADO**: Extraer filename del nuevo formato [IMAGEN_ENVIADO] uploads/filename

  // Primero intentar el nuevo formato con [IMAGEN_ENVIADO]
  let match = text.match(/\[IMAGEN_ENVIADO\]\s*(uploads\/[^\s]+\.(?:png|jpe?g|gif|webp))/i);
  if (match) {
    console.log(`📸 [ChatPanel] Imagen extraída del nuevo formato: ${match[1]}`);
    return match[1]; // Devolver 'uploads/filename'
  }

  // Segundo formato: [IMAGEN_PROCESADA] URL: /uploads/filename
  match = text.match(/\[IMAGEN_PROCESADA\]\s*URL:\s*\/uploads\/([^\s]+\.(?:png|jpe?g|gif|webp))/i);
  if (match) {
    console.log(`📸 [ChatPanel] Imagen extraída del formato procesada: ${match[1]}`);
    return match[1]; // Devolver solo el filename para este formato
  }

  // Fallback: formato anterior para compatibilidad
  match = text.match(/\/uploads\/([^\s]+\.(?:png|jpe?g|gif|webp))(?:\s|$)/i);
  if (match) {
    console.log(`📸 [ChatPanel] Imagen extraída del formato anterior: ${match[1]}`);
    return match[1]; // Devolver solo el filename para este formato
  }

  return null;
};

const extractPdfFilename = (text = '') => {
  console.log(`📄 [ChatPanel] Buscando PDF en texto: "${text}"`);

  // Primero intentar el nuevo formato con [PDF_ENVIADO] uploads/filename
  let match = text.match(/\[PDF_ENVIADO\]\s*(uploads\/[^\s]+\.pdf)/i);
  if (match) {
    console.log(`📄 [ChatPanel] PDF extraído del nuevo formato: ${match[1]}`);
    return match[1]; // Devolver 'uploads/filename'
  }

  // Segundo formato: [PDF_PROCESADO] URL: /uploads/filename
  match = text.match(/\[PDF_PROCESADO\]\s*URL:\s*(\/uploads\/[^\s]+\.pdf)/i);
  if (match) {
    console.log(`📄 [ChatPanel] PDF extraído del formato procesado: ${match[1]}`);
    return match[1]; // Devolver la ruta completa /uploads/filename
  }

  // Tercer formato: [PDF_PROCESADO] /uploads/filename (sin URL:)
  match = text.match(/\[PDF_PROCESADO\]\s*(\/uploads\/[^\s]+\.pdf)/i);
  if (match) {
    console.log(`📄 [ChatPanel] PDF extraído del formato procesado sin URL: ${match[1]}`);
    return match[1]; // Devolver la ruta completa /uploads/filename
  }

  // Cuarto formato: [PDF_PROCESADO] uploads/filename (sin barra inicial)
  match = text.match(/\[PDF_PROCESADO\]\s*(uploads\/[^\s]+\.pdf)/i);
  if (match) {
    console.log(`📄 [ChatPanel] PDF extraído del formato procesado con uploads: ${match[1]}`);
    return match[1];
  }

  // Quinto formato: cualquier mención de archivo PDF
  match = text.match(/([^\s]*\.pdf)/i);
  if (match) {
    console.log(`📄 [ChatPanel] PDF extraído de formato genérico: ${match[1]}`);
    return match[1];
  }

  // Fallback: formato anterior para compatibilidad
  match = text.match(/(\/uploads\/[^\s]+\.pdf)(?:\s|$)/i);
  if (match) {
    console.log(`📄 [ChatPanel] PDF extraído del formato anterior: ${match[1]}`);
    return match[1]; // Devolver la ruta completa /uploads/filename
  }

  console.log(`📄 [ChatPanel] No se encontró PDF en el texto`);
  return null;
};

// Función para extraer archivos de Word (DOC, DOCX)
const extractWordFilename = (text = '') => {
  console.log(`📝 [ChatPanel] Analizando texto para Word: ${text}`);

  // Primero intentar el nuevo formato con [WORD_ENVIADO] uploads/filename
  let match = text.match(/\[(?:WORD|DOC)_ENVIADO\]\s*(uploads\/[^\s]+\.docx?)/i);
  if (match) {
    console.log(`📝 [ChatPanel] Word extraído del nuevo formato: ${match[1]}`);
    return match[1]; // Devolver 'uploads/filename'
  }

  // Segundo formato: [WORD_PROCESADO] URL: /uploads/filename
  match = text.match(/\[(?:WORD|DOC)_PROCESADO\]\s*URL:\s*\/uploads\/([^\s]+\.docx?)/i);
  if (match) {
    console.log(`📝 [ChatPanel] Word extraído del formato procesado con URL: ${match[1]}`);
    return match[1]; // Devolver solo el filename para este formato
  }

  // Tercer formato: [WORD_PROCESADO] /uploads/filename (sin URL:)
  match = text.match(/\[(?:WORD|DOC)_PROCESADO\]\s*\/uploads\/([^\s]+\.docx?)/i);
  if (match) {
    console.log(`📝 [ChatPanel] Word extraído del formato procesado sin URL: ${match[1]}`);
    return match[1];
  }

  // Cuarto formato: [WORD_PROCESADO] uploads/filename (sin barra inicial)
  match = text.match(/\[(?:WORD|DOC)_PROCESADO\]\s*(uploads\/[^\s]+\.docx?)/i);
  if (match) {
    console.log(`📝 [ChatPanel] Word extraído del formato procesado con uploads: ${match[1]}`);
    return match[1];
  }

  // Quinto formato: cualquier mención de archivo Word (.doc o .docx)
  match = text.match(/([^\s]*\.docx?)/i);
  if (match) {
    console.log(`📝 [ChatPanel] Word extraído de formato genérico: ${match[1]}`);
    return match[1];
  }

  // Fallback: formato anterior para compatibilidad
  match = text.match(/\/uploads\/([^\s]+\.docx?)(?:\s|$)/i);
  if (match) {
    console.log(`📝 [ChatPanel] Word extraído del formato anterior: ${match[1]}`);
    return match[1]; // Devolver solo el filename para este formato
  }

  console.log(`📝 [ChatPanel] No se encontró Word en el texto`);
  return null;
};

// Función para extraer archivos de Excel (XLS, XLSX)
const extractExcelFilename = (text = '') => {
  // Primero intentar el nuevo formato con [EXCEL_ENVIADO] uploads/filename
  let match = text.match(/\[(?:EXCEL|XLS)_ENVIADO\]\s*(uploads\/[^\s]+\.xlsx?)/i);
  if (match) {
    console.log(`📊 [ChatPanel] Excel extraído del nuevo formato: ${match[1]}`);
    return match[1]; // Devolver 'uploads/filename'
  }

  // Segundo formato: [EXCEL_PROCESADO] URL: /uploads/filename
  match = text.match(/\[(?:EXCEL|XLS)_PROCESADO\]\s*URL:\s*\/uploads\/([^\s]+\.xlsx?)/i);
  if (match) {
    console.log(`📊 [ChatPanel] Excel extraído del formato procesado: ${match[1]}`);
    return match[1]; // Devolver solo el filename para este formato
  }

  // Fallback: formato anterior
  match = text.match(/\/uploads\/([^\s]+\.xlsx?)(?:\s|$)/i);
  return match ? match[1] : null;
};

// Función para extraer archivos de PowerPoint (PPT, PPTX)
const extractPowerPointFilename = (text = '') => {
  // Primero intentar el nuevo formato con [PPT_ENVIADO] uploads/filename
  let match = text.match(/\[(?:PPT|POWERPOINT)_ENVIADO\]\s*(uploads\/[^\s]+\.pptx?)/i);
  if (match) {
    console.log(`📽️ [ChatPanel] PowerPoint extraído del nuevo formato: ${match[1]}`);
    return match[1]; // Devolver 'uploads/filename'
  }

  // Segundo formato: [PPT_PROCESADO] URL: /uploads/filename
  match = text.match(/\[(?:PPT|POWERPOINT)_PROCESADO\]\s*URL:\s*\/uploads\/([^\s]+\.pptx?)/i);
  if (match) {
    console.log(`📽️ [ChatPanel] PowerPoint extraído del formato procesado: ${match[1]}`);
    return match[1]; // Devolver solo el filename para este formato
  }

  // Fallback: formato anterior
  match = text.match(/\/uploads\/([^\s]+\.pptx?)(?:\s|$)/i);
  return match ? match[1] : null;
};

// Función para extraer archivos de audio
const extractAudioFilename = (text = '') => {
  // Primero intentar el nuevo formato con [AUDIO_ENVIADO] uploads/filename
  let match = text.match(/\[(?:AUDIO|MP3)_ENVIADO\]\s*(uploads\/[^\s]+\.(?:mp3|wav|ogg|m4a|aac))/i);
  if (match) {
    console.log(`🎵 [ChatPanel] Audio extraído del nuevo formato: ${match[1]}`);
    return match[1]; // Devolver 'uploads/filename'
  }

  // Segundo formato: [AUDIO_PROCESADO] URL: /uploads/filename
  match = text.match(/\[(?:AUDIO|MP3)_PROCESADO\]\s*URL:\s*\/uploads\/([^\s]+\.(?:mp3|wav|ogg|m4a|aac))/i);
  if (match) {
    console.log(`🎵 [ChatPanel] Audio extraído del formato procesado: ${match[1]}`);
    return match[1]; // Devolver solo el filename para este formato
  }

  // Fallback: formato anterior
  match = text.match(/\/uploads\/([^\s]+\.(?:mp3|wav|ogg|m4a|aac))(?:\s|$)/i);
  return match ? match[1] : null;
};

// Función para extraer archivos de video
const extractVideoFilename = (text = '') => {
  // Primero intentar el nuevo formato con [VIDEO_ENVIADO] uploads/filename
  let match = text.match(/\[(?:VIDEO|MP4)_ENVIADO\]\s*(uploads\/[^\s]+\.(?:mp4|avi|mov|wmv|flv|webm))/i);
  if (match) {
    console.log(`🎬 [ChatPanel] Video extraído del nuevo formato: ${match[1]}`);
    return match[1]; // Devolver 'uploads/filename'
  }

  // Segundo formato: [VIDEO_PROCESADO] URL: /uploads/filename
  match = text.match(/\[(?:VIDEO|MP4)_PROCESADO\]\s*URL:\s*\/uploads\/([^\s]+\.(?:mp4|avi|mov|wmv|flv|webm))/i);
  if (match) {
    console.log(`🎬 [ChatPanel] Video extraído del formato procesado: ${match[1]}`);
    return match[1]; // Devolver solo el filename para este formato
  }

  // Fallback: formato anterior
  match = text.match(/\/uploads\/([^\s]+\.(?:mp4|avi|mov|wmv|flv|webm))(?:\s|$)/i);
  return match ? match[1] : null;
};

// Función para extraer cualquier tipo de archivo (copiando lógica de extractUploadFilename)
const extractAnyFilename = (text = '') => {
  // **ACTUALIZADO**: Extraer filename del nuevo formato [ARCHIVO_ENVIADO] uploads/filename

  // Primero intentar el nuevo formato con [ARCHIVO_ENVIADO]
  let match = text.match(/\[ARCHIVO_ENVIADO\]\s*(uploads\/[^\s]+\.[^\s]+)/i);
  if (match) {
    console.log(`📎 [ChatPanel] Archivo extraído del nuevo formato: ${match[1]}`);
    return match[1]; // Devolver 'uploads/filename'
  }

  // Segundo formato: [ARCHIVO_PROCESADO] URL: /uploads/filename
  match = text.match(/\[ARCHIVO_PROCESADO\]\s*URL:\s*\/uploads\/([^\s]+\.[^\s]+)/i);
  if (match) {
    console.log(`📎 [ChatPanel] Archivo extraído del formato procesado: ${match[1]}`);
    return match[1]; // Devolver solo el filename para este formato
  }

  // Fallback: formato anterior para compatibilidad
  match = text.match(/\/uploads\/([^\s]+\.[^\s]+)(?:\s|$)/i);
  if (match) {
    console.log(`📎 [ChatPanel] Archivo extraído del formato anterior: ${match[1]}`);
    return match[1]; // Devolver solo el filename para este formato
  }

  return null;
};

// --- Componente Mensaje Estilo WhatsApp con Colores Originales ---
// 🚀 FUNCIÓN HELPER PARA OBTENER EL ICONO DE ESTADO DEL MENSAJE
const getMessageStatusIcon = (estadoMensaje) => {
  const estado = estadoMensaje || 'enviado'; // Por defecto 'enviado'
  const grayColor = '#8696a0';
  const blueColor = '#0084ff'; // Azul más intenso para que resalte sobre el fondo celeste
  const redColor = '#ff5252'; // Rojo suave acorde al diseño del chat
  const iconStyle = { fontSize: '1.1rem' }; // Iconos más grandes
  
  switch (estado) {
    case 'pendiente':
      // Reloj gris: El mensaje está pendiente de envío
      return <i className="bi bi-clock" style={{ color: grayColor, ...iconStyle }}></i>;
    
    case 'enviado':
      // Un tick gris: El mensaje ha sido enviado (usando bi-check-lg para que se vea más grueso)
      return <i className="bi bi-check-lg" style={{ color: grayColor, ...iconStyle }}></i>;
    
    case 'entregado':
      // Doble check gris: El mensaje ha sido entregado
      return <i className="bi bi-check2-all" style={{ color: grayColor, ...iconStyle }}></i>;
    
    case 'visto':
      // Doble check azul intenso: El mensaje ha sido leído
      return <i className="bi bi-check2-all" style={{ color: blueColor, ...iconStyle }}></i>;
    
    case 'fallido':
      // Icono de error para mensajes fallidos
      return <i className="bi bi-x-circle" style={{ color: redColor, ...iconStyle }}></i>;
    
    case 'recibido':
      // Doble check gris (similar a entregado)
      return <i className="bi bi-check2-all" style={{ color: grayColor, ...iconStyle }}></i>;
    
    default:
      // Por defecto, mostrar como enviado
      return <i className="bi bi-check-lg" style={{ color: grayColor, ...iconStyle }}></i>;
  }
};

const Mensaje = React.memo(({ msg }) => {
  const { autor, sender, texto, hora, timestamp, imageUrl, archivoUrl, tipoArchivo, estadoMensaje } = msg;
  const [currentSrc, setCurrentSrc] = useState(null);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
  const [currentWordUrl, setCurrentWordUrl] = useState(null);
  const [currentExcelUrl, setCurrentExcelUrl] = useState(null);
  const [currentPowerPointUrl, setCurrentPowerPointUrl] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Nueva lógica basada en archivoUrl y tipoArchivo
  useEffect(() => {
    if (archivoUrl && tipoArchivo) {
      console.log(`📁 [Mensaje] Archivo detectado: ${archivoUrl}, Tipo: ${tipoArchivo}`);

      switch (tipoArchivo) {
        case 'imagen':
          setCurrentSrc(archivoUrl);
          break;
        case 'documento':
          // Determinar el subtipo basado en la extensión del archivo
          const extension = archivoUrl.split('.').pop().toLowerCase();
          if (extension === 'pdf') {
            setCurrentPdfUrl(archivoUrl);
          } else if (['doc', 'docx'].includes(extension)) {
            setCurrentWordUrl(archivoUrl);
          } else if (['xls', 'xlsx'].includes(extension)) {
            setCurrentExcelUrl(archivoUrl);
          } else if (['ppt', 'pptx'].includes(extension)) {
            setCurrentPowerPointUrl(archivoUrl);
          }
          break;
        case 'video':
          // Para videos podrías implementar un reproductor específico
          console.log(`🎥 [Mensaje] Video detectado: ${archivoUrl}`);
          break;
        case 'audio':
          // Para audio podrías implementar un reproductor específico
          console.log(`🎵 [Mensaje] Audio detectado: ${archivoUrl}`);
          break;
        default:
          console.log(`📎 [Mensaje] Tipo de archivo no manejado: ${tipoArchivo}`);
      }
      return;
    }

    // Fallback: Extraer diferentes tipos de archivos del texto si existen (compatibilidad)
    const imgFilename = extractUploadFilename(texto);
    const pdfFilename = extractPdfFilename(texto);
    const wordFilename = extractWordFilename(texto);
    const excelFilename = extractExcelFilename(texto);
    const powerPointFilename = extractPowerPointFilename(texto);

    if (imgFilename) {
      const imgUrl = imgFilename.startsWith('uploads/') ? `/${imgFilename}` : `${UPLOADS_HOST}/${imgFilename}`;
      setCurrentSrc(imgUrl);
    }

    if (pdfFilename) {
      const pdfUrl = pdfFilename.startsWith('uploads/') ? `/${pdfFilename}` :
        pdfFilename.startsWith('/uploads/') ? pdfFilename : `${UPLOADS_HOST}/${pdfFilename}`;
      setCurrentPdfUrl(pdfUrl);
    }

    if (wordFilename) {
      const wordUrl = wordFilename.startsWith('uploads/') ? `/${wordFilename}` : `${UPLOADS_HOST}/${wordFilename}`;
      setCurrentWordUrl(wordUrl);
    }

    if (excelFilename) {
      const excelUrl = excelFilename.startsWith('uploads/') ? `/${excelFilename}` : `${UPLOADS_HOST}/${excelFilename}`;
      setCurrentExcelUrl(excelUrl);
    }

    if (powerPointFilename) {
      const pptUrl = powerPointFilename.startsWith('uploads/') ? `/${powerPointFilename}` : `${UPLOADS_HOST}/${powerPointFilename}`;
      setCurrentPowerPointUrl(pptUrl);
    }
  }, [archivoUrl, tipoArchivo, texto]);

  // Mantener lógica existente para compatibilidad
  const imgFilename = useMemo(() => extractUploadFilename(texto), [texto]);
  const pdfFilename = useMemo(() => {
    console.log(`📄 [ChatPanel] Analizando texto para PDF: "${texto}"`);
    return extractPdfFilename(texto);
  }, [texto]);
  const wordFilename = useMemo(() => extractWordFilename(texto), [texto]);
  const excelFilename = useMemo(() => extractExcelFilename(texto), [texto]);
  const powerPointFilename = useMemo(() => extractPowerPointFilename(texto), [texto]);
  const audioFilename = useMemo(() => extractAudioFilename(texto), [texto]);
  const videoFilename = useMemo(() => extractVideoFilename(texto), [texto]);
  const anyFilename = useMemo(() => extractAnyFilename(texto), [texto]);

  // URLs para los archivos
  const imgUrl = useMemo(() => {
    if (imgFilename) {
      // Si imgFilename ya incluye 'uploads/', usarlo directamente
      if (imgFilename.startsWith('uploads/')) {
        return `/${imgFilename}`;
      } else {
        return `${UPLOADS_HOST}/${imgFilename}`;
      }
    }
    return null;
  }, [imgFilename]);

  const pdfUrl = useMemo(() => {
    if (pdfFilename) {
      console.log(`📄 [ChatPanel] Generando pdfUrl para: ${pdfFilename}`);
      // Si pdfFilename ya incluye 'uploads/', usarlo directamente
      if (pdfFilename.startsWith('uploads/')) {
        const url = `/${pdfFilename}`;
        console.log(`📄 [ChatPanel] URL generada (con uploads/): ${url}`);
        return url;
      } else if (pdfFilename.startsWith('/uploads/')) {
        // Si ya tiene la barra inicial, usarlo tal como está
        const url = pdfFilename;
        console.log(`📄 [ChatPanel] URL generada (ya con /uploads/): ${url}`);
        return url;
      } else {
        const url = `${UPLOADS_HOST}/${pdfFilename}`;
        console.log(`📄 [ChatPanel] URL generada (sin uploads/): ${url}`);
        return url;
      }
    }
    console.log(`📄 [ChatPanel] No se encontró pdfFilename, pdfUrl será null`);
    return null;
  }, [pdfFilename]);

  const wordUrl = useMemo(() => {
    if (wordFilename) {
      // Si wordFilename ya incluye 'uploads/', usarlo directamente
      if (wordFilename.startsWith('uploads/')) {
        return `/${wordFilename}`;
      } else {
        return `${UPLOADS_HOST}/${wordFilename}`;
      }
    }
    return null;
  }, [wordFilename]);

  const excelUrl = useMemo(() => {
    if (excelFilename) {
      // Si excelFilename ya incluye 'uploads/', usarlo directamente
      if (excelFilename.startsWith('uploads/')) {
        return `/${excelFilename}`;
      } else {
        return `${UPLOADS_HOST}/${excelFilename}`;
      }
    }
    return null;
  }, [excelFilename]);

  const powerPointUrl = useMemo(() => {
    if (powerPointFilename) {
      // Si powerPointFilename ya incluye 'uploads/', usarlo directamente
      if (powerPointFilename.startsWith('uploads/')) {
        return `/${powerPointFilename}`;
      } else {
        return `${UPLOADS_HOST}/${powerPointFilename}`;
      }
    }
    return null;
  }, [powerPointFilename]);

  const audioUrl = useMemo(() => {
    if (audioFilename) {
      // Si audioFilename ya incluye 'uploads/', usarlo directamente
      if (audioFilename.startsWith('uploads/')) {
        return `/${audioFilename}`;
      } else {
        return `${UPLOADS_HOST}/${audioFilename}`;
      }
    }
    return null;
  }, [audioFilename]);

  const videoUrl = useMemo(() => {
    if (videoFilename) {
      // Si videoFilename ya incluye 'uploads/', usarlo directamente
      if (videoFilename.startsWith('uploads/')) {
        return `/${videoFilename}`;
      } else {
        return `${UPLOADS_HOST}/${videoFilename}`;
      }
    }
    return null;
  }, [videoFilename]);

  const anyFileUrl = useMemo(() => {
    if (anyFilename) {
      // Si anyFilename ya incluye 'uploads/', usarlo directamente
      if (anyFilename.startsWith('uploads/')) {
        return `/${anyFilename}`;
      } else {
        return `${UPLOADS_HOST}/${anyFilename}`;
      }
    }
    return null;
  }, [anyFilename]);

  // Limpiar texto de rutas de archivos y texto técnico
  const cleanText = useMemo(() => {
    let clean = texto;

    // **NUEVO**: Convertir \n literales (string) a saltos de línea reales
    // Primero manejar \\n (backslash escapado) y luego \n (backslash normal)
    clean = clean.replace(/\\n/g, '\n');

    // **ACTUALIZADO**: Limpiar el nuevo formato [IMAGEN_PROCESADA] URL: /uploads/filename [CLASIFICACIÓN] category
    if (clean.includes('[IMAGEN_PROCESADA]')) {
      // Eliminar todo desde [IMAGEN_PROCESADA] hasta el final de la línea
      clean = clean.replace(/\[IMAGEN_PROCESADA\].*$/g, '').trim();
    }

    // **NUEVO**: Limpiar etiquetas de OCR, clasificación y archivos enviados
    clean = clean.replace(/\[OCR\].*$/g, '').trim();
    clean = clean.replace(/\[CLASIFICACIÓN\].*$/g, '').trim();
    clean = clean.replace(/\[IMAGEN_ENVIADO\].*$/g, '').trim();
    clean = clean.replace(/\[PDF_ENVIADO\].*$/g, '').trim();
    clean = clean.replace(/\[(?:WORD|DOC)_ENVIADO\].*$/g, '').trim();
    clean = clean.replace(/\[(?:EXCEL|XLS)_ENVIADO\].*$/g, '').trim();
    clean = clean.replace(/\[(?:PPT|POWERPOINT)_ENVIADO\].*$/g, '').trim();
    clean = clean.replace(/\[(?:AUDIO|MP3)_ENVIADO\].*$/g, '').trim();
    clean = clean.replace(/\[(?:VIDEO|MP4)_ENVIADO\].*$/g, '').trim();
    clean = clean.replace(/\[ARCHIVO_ENVIADO\].*$/g, '').trim();

    // **NUEVO**: Limpiar etiquetas de archivos procesados
    clean = clean.replace(/\[PDF_PROCESADO\].*$/g, '').trim();
    clean = clean.replace(/\[(?:WORD|DOC)_PROCESADO\].*$/g, '').trim();
    clean = clean.replace(/\[(?:EXCEL|XLS)_PROCESADO\].*$/g, '').trim();
    clean = clean.replace(/\[(?:PPT|POWERPOINT)_PROCESADO\].*$/g, '').trim();
    clean = clean.replace(/\[(?:AUDIO|MP3)_PROCESADO\].*$/g, '').trim();
    clean = clean.replace(/\[(?:VIDEO|MP4)_PROCESADO\].*$/g, '').trim();
    clean = clean.replace(/\[ARCHIVO_PROCESADO\].*$/g, '').trim();

    // Función auxiliar para escapar caracteres especiales en regex
    const escapeRegex = (str) => {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    // Limpiar rutas de todos los tipos de archivos (ambos formatos)
    if (pdfFilename) {
      // Limpiar tanto /uploads/filename como uploads/filename
      if (pdfFilename.startsWith('uploads/')) {
        const escapedFilename = escapeRegex(pdfFilename);
        clean = clean.replace(new RegExp(escapedFilename, 'g'), '').trim();
        clean = clean.replace(new RegExp(`/${escapedFilename}`, 'g'), '').trim();
      } else {
        const escapedFilename = escapeRegex(pdfFilename);
        clean = clean.replace(new RegExp(`/uploads/${escapedFilename}`, 'g'), '').trim();
        clean = clean.replace(new RegExp(`uploads/${escapedFilename}`, 'g'), '').trim();
      }
    }
    if (wordFilename) {
      if (wordFilename.startsWith('uploads/')) {
        const escapedFilename = escapeRegex(wordFilename);
        clean = clean.replace(new RegExp(escapedFilename, 'g'), '').trim();
        clean = clean.replace(new RegExp(`/${escapedFilename}`, 'g'), '').trim();
      } else {
        const escapedFilename = escapeRegex(wordFilename);
        clean = clean.replace(new RegExp(`/uploads/${escapedFilename}`, 'g'), '').trim();
        clean = clean.replace(new RegExp(`uploads/${escapedFilename}`, 'g'), '').trim();
      }
    }
    if (excelFilename) {
      if (excelFilename.startsWith('uploads/')) {
        const escapedFilename = escapeRegex(excelFilename);
        clean = clean.replace(new RegExp(escapedFilename, 'g'), '').trim();
        clean = clean.replace(new RegExp(`/${escapedFilename}`, 'g'), '').trim();
      } else {
        const escapedFilename = escapeRegex(excelFilename);
        clean = clean.replace(new RegExp(`/uploads/${escapedFilename}`, 'g'), '').trim();
        clean = clean.replace(new RegExp(`uploads/${escapedFilename}`, 'g'), '').trim();
      }
    }
    if (powerPointFilename) {
      if (powerPointFilename.startsWith('uploads/')) {
        const escapedFilename = escapeRegex(powerPointFilename);
        clean = clean.replace(new RegExp(escapedFilename, 'g'), '').trim();
        clean = clean.replace(new RegExp(`/${escapedFilename}`, 'g'), '').trim();
      } else {
        const escapedFilename = escapeRegex(powerPointFilename);
        clean = clean.replace(new RegExp(`/uploads/${escapedFilename}`, 'g'), '').trim();
        clean = clean.replace(new RegExp(`uploads/${escapedFilename}`, 'g'), '').trim();
      }
    }
    if (audioFilename) {
      if (audioFilename.startsWith('uploads/')) {
        const escapedFilename = escapeRegex(audioFilename);
        clean = clean.replace(new RegExp(escapedFilename, 'g'), '').trim();
        clean = clean.replace(new RegExp(`/${escapedFilename}`, 'g'), '').trim();
      } else {
        const escapedFilename = escapeRegex(audioFilename);
        clean = clean.replace(new RegExp(`/uploads/${escapedFilename}`, 'g'), '').trim();
        clean = clean.replace(new RegExp(`uploads/${escapedFilename}`, 'g'), '').trim();
      }
    }
    if (videoFilename) {
      if (videoFilename.startsWith('uploads/')) {
        const escapedFilename = escapeRegex(videoFilename);
        clean = clean.replace(new RegExp(escapedFilename, 'g'), '').trim();
        clean = clean.replace(new RegExp(`/${escapedFilename}`, 'g'), '').trim();
      } else {
        const escapedFilename = escapeRegex(videoFilename);
        clean = clean.replace(new RegExp(`/uploads/${escapedFilename}`, 'g'), '').trim();
        clean = clean.replace(new RegExp(`uploads/${escapedFilename}`, 'g'), '').trim();
      }
    }

    // Limpiar rutas de imágenes (formato anterior)
    if (imageUrl) {
      const imgFilename = extractUploadFilename(texto);
      if (imgFilename) {
        if (imgFilename.startsWith('uploads/')) {
          const escapedFilename = escapeRegex(imgFilename);
          clean = clean.replace(new RegExp(escapedFilename, 'g'), '').trim();
          clean = clean.replace(new RegExp(`/${escapedFilename}`, 'g'), '').trim();
        } else {
          const escapedFilename = escapeRegex(imgFilename);
          clean = clean.replace(new RegExp(`/uploads/${escapedFilename}`, 'g'), '').trim();
          clean = clean.replace(new RegExp(`uploads/${escapedFilename}`, 'g'), '').trim();
        }
      }
    }

    // Limpiar rutas de archivos genéricos
    if (anyFilename) {
      if (anyFilename.startsWith('uploads/')) {
        const escapedFilename = escapeRegex(anyFilename);
        clean = clean.replace(new RegExp(escapedFilename, 'g'), '').trim();
        clean = clean.replace(new RegExp(`/${escapedFilename}`, 'g'), '').trim();
      } else {
        const escapedFilename = escapeRegex(anyFilename);
        clean = clean.replace(new RegExp(`/uploads/${escapedFilename}`, 'g'), '').trim();
        clean = clean.replace(new RegExp(`uploads/${escapedFilename}`, 'g'), '').trim();
      }
    }

    // **MEJORADO**: Si después de limpiar el texto queda vacío y hay algún archivo, no mostrar texto
    if ((imgUrl || imageUrl || pdfUrl || wordUrl || excelUrl || powerPointUrl || audioUrl || videoUrl || anyFileUrl) &&
      (!clean || clean.length === 0 || clean === '[IMAGEN_PROCESADA]' || clean === '[PDF_PROCESADO]' ||
        clean === '[WORD_PROCESADO]' || clean === '[EXCEL_PROCESADO]' || clean === '[PPT_PROCESADO]' ||
        clean === '[AUDIO_PROCESADO]' || clean === '[VIDEO_PROCESADO]' || clean === '[ARCHIVO_PROCESADO]')) {
      return '';
    }

    return clean;
  }, [texto, pdfFilename, wordFilename, excelFilename, powerPointFilename, audioFilename, videoFilename, anyFilename, imgUrl, imageUrl, pdfUrl, wordUrl, excelUrl, powerPointUrl, audioUrl, videoUrl, anyFileUrl]);

  // Sincronizar currentSrc con imgUrl o imageUrl
  useEffect(() => {
    // Priorizar imgUrl (extraída del texto) sobre imageUrl (del prop)
    setCurrentSrc(imgUrl || imageUrl);
  }, [imgUrl, imageUrl]);

  // Sincronizar currentPdfUrl con pdfUrl
  useEffect(() => {
    console.log(`📄 [ChatPanel] Actualizando currentPdfUrl: ${pdfUrl}`);
    setCurrentPdfUrl(pdfUrl);
  }, [pdfUrl]);

  // Sincronizar currentWordUrl con wordUrl
  useEffect(() => {
    setCurrentWordUrl(wordUrl);
  }, [wordUrl]);

  // Sincronizar currentExcelUrl con excelUrl
  useEffect(() => {
    setCurrentExcelUrl(excelUrl);
  }, [excelUrl]);

  // Sincronizar currentPowerPointUrl con powerPointUrl
  useEffect(() => {
    setCurrentPowerPointUrl(powerPointUrl);
  }, [powerPointUrl]);

  const handleImageError = useCallback(() => {
    setCurrentSrc('/placeholder-image.png');
  }, []);

  const handleImageClick = useCallback(() => {
    if (currentSrc) {
      setShowImageModal(true);
      setZoomLevel(1);
    }
  }, [currentSrc]);

  const handleCloseModal = useCallback(() => setShowImageModal(false), []);
  const handleZoomIn = useCallback(() => setZoomLevel(prev => Math.min(prev + 0.5, 4)), []);
  const handleZoomOut = useCallback(() => setZoomLevel(prev => Math.max(prev - 0.5, 0.25)), []);
  const handleResetZoom = useCallback(() => setZoomLevel(1), []);

  // 🖱️ DOBLE CLIC PARA ZOOM COMO WHATSAPP
  const handleImageDoubleClick = useCallback(() => {
    if (zoomLevel === 1) {
      setZoomLevel(2.5); // Zoom in al hacer doble clic
    } else {
      setZoomLevel(1); // Reset zoom al hacer doble clic nuevamente
    }
  }, [zoomLevel]);

  const handlePdfDownload = useCallback(() => {
    if (currentPdfUrl) {
      window.open(currentPdfUrl, '_blank');
    }
  }, [currentPdfUrl]);

  // 📅 USAR TIMESTAMP PARA MOSTRAR LA HORA CORRECTAMENTE
  const timeOnly = formatTimeOnly(timestamp || hora);
  const isFromBot = autor === 'bot';

  // 🏷️ FUNCIÓN HELPER PARA MAPEAR SENDER A ETIQUETA
  const getSenderLabel = (senderType) => {
    if (!senderType) return null;
    const labelMap = {
      'system': 'sistema',
      'chatbot': 'chatbot',
      'asesor': 'asesor'
    };
    return labelMap[senderType] || null;
  };

  const senderLabel = isFromBot ? getSenderLabel(sender) : null;

  return (
    <div className={`whatsapp-message ${isFromBot ? 'outgoing' : 'incoming'}`}>
      <div className="message-bubble">
        <div className="message-content">
          {/* Avatar solo para mensajes entrantes en desktop */}
          <div className="message-header d-none d-md-block">
            <strong className="sender-name">
              {isFromBot ? 'Chat Bot' : 'Cliente'}
            </strong>
            {senderLabel && (
              <span className="sender-label" style={{
                fontSize: '0.7rem',
                color: '#8696a0',
                fontWeight: 'normal',
                marginLeft: '6px',
                opacity: 0.7
              }}>
                {senderLabel}
              </span>
            )}
          </div>

          {/* Imagen */}
          {currentSrc && currentSrc !== '/placeholder-image.png' && (
            <div className="message-media">
              <img
                src={currentSrc}
                alt="Imagen compartida"
                className="message-image"
                onClick={handleImageClick}
                onError={handleImageError}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleImageClick()}
              />
            </div>
          )}

          {/* PDF */}
          {currentPdfUrl && (
            <div className="message-media">
              <div
                className="message-image pdf-preview"
                onClick={handlePdfDownload}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handlePdfDownload()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '20px',
                  cursor: 'pointer',
                  minHeight: '120px'
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <i className="bi bi-file-earmark-pdf-fill" style={{ fontSize: '48px', color: '#dc3545', marginBottom: '8px' }}></i>
                  <div style={{ fontSize: '14px', fontWeight: '500' }}>Documento PDF</div>
                  <div style={{ fontSize: '12px', color: '#6c757d' }}>Click para abrir</div>
                </div>
              </div>
            </div>
          )}

          {/* Word Documents */}
          {currentWordUrl && (
            <div className="message-media">
              <div
                className="message-image word-preview"
                onClick={() => window.open(currentWordUrl, '_blank')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && window.open(currentWordUrl, '_blank')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '20px',
                  cursor: 'pointer',
                  minHeight: '120px'
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <i className="bi bi-file-earmark-word-fill" style={{ fontSize: '48px', color: '#2b579a', marginBottom: '8px' }}></i>
                  <div style={{ fontSize: '14px', fontWeight: '500' }}>Documento Word</div>
                  <div style={{ fontSize: '12px', color: '#6c757d' }}>Click para descargar</div>
                </div>
              </div>
            </div>
          )}

          {/* Excel Documents */}
          {currentExcelUrl && (
            <div className="message-media">
              <div className="pdf-attachment" onClick={() => window.open(currentExcelUrl, '_blank')}>
                <div className="pdf-icon excel-icon">
                  <i className="bi bi-file-earmark-excel-fill"></i>
                </div>
                <div className="pdf-details">
                  <span className="pdf-name">Hoja de Cálculo</span>
                  <span className="pdf-size">Click para descargar</span>
                </div>
              </div>
            </div>
          )}

          {/* PowerPoint Documents */}
          {currentPowerPointUrl && (
            <div className="message-media">
              <div className="pdf-attachment" onClick={() => window.open(currentPowerPointUrl, '_blank')}>
                <div className="pdf-icon powerpoint-icon">
                  <i className="bi bi-file-earmark-ppt-fill"></i>
                </div>
                <div className="pdf-details">
                  <span className="pdf-name">Presentación</span>
                  <span className="pdf-size">Click para descargar</span>
                </div>
              </div>
            </div>
          )}

          {/* Audio Files */}
          {audioUrl && (
            <div className="message-media">
              <div className="pdf-attachment">
                <div className="pdf-icon audio-icon">
                  <i className="bi bi-music-note-beamed"></i>
                </div>
                <div className="pdf-details">
                  <span className="pdf-name">Archivo de Audio</span>
                  <audio controls style={{ width: '100%', marginTop: '5px' }}>
                    <source src={audioUrl} />
                    Tu navegador no soporta el elemento de audio.
                  </audio>
                </div>
              </div>
            </div>
          )}

          {/* Video Files */}
          {videoUrl && (
            <div className="message-media">
              <div className="pdf-attachment">
                <div className="pdf-icon video-icon">
                  <i className="bi bi-play-circle-fill"></i>
                </div>
                <div className="pdf-details">
                  <span className="pdf-name">Archivo de Video</span>
                  <video controls style={{ width: '100%', maxWidth: '300px', marginTop: '5px' }}>
                    <source src={videoUrl} />
                    Tu navegador no soporta el elemento de video.
                  </video>
                </div>
              </div>
            </div>
          )}

          {/* Generic Files */}
          {anyFileUrl && (
            <div className="message-media">
              <div className="pdf-attachment" onClick={() => window.open(anyFileUrl, '_blank')}>
                <div className="pdf-icon">
                  <i className="bi bi-file-earmark-fill"></i>
                </div>
                <div className="pdf-details">
                  <span className="pdf-name">Documento</span>
                  <span className="pdf-size">Click para descargar</span>
                </div>
              </div>
            </div>
          )}

          {/* Texto del mensaje */}
          {cleanText && (
            <div className="message-text">
              <p style={{ whiteSpace: 'pre-line' }}>{cleanText}</p>
            </div>
          )}

          {/* Información del mensaje (hora y estado) */}
          <div className="message-info">
            <span className="message-time">{timeOnly}</span>
            {isFromBot && (
              <div className="message-status">
                {getMessageStatusIcon(estadoMensaje)}
              </div>
            )}
          </div>
        </div>

        {/* Cola de la burbuja */}
        <div className={`message-tail ${isFromBot ? 'outgoing-tail' : 'incoming-tail'}`}></div>
      </div>

      {/* Modal de imagen mejorado */}
      {showImageModal && currentSrc && currentSrc !== '/placeholder-image.png' && (
        <Modal
          show={showImageModal}
          onHide={handleCloseModal}
          centered
          size="lg"
          className="chat-image-modal"
          dialogClassName="chat-image-modal-dialog"
        >
          <Modal.Header closeButton className="chat-image-modal-header">
            <Modal.Title>Vista detallada</Modal.Title>
            <div className="zoom-level-indicator">
              {Math.round(zoomLevel * 100)}%
            </div>
          </Modal.Header>
          <Modal.Body className="chat-image-modal-body">
            <div className="zoom-controls-container">
              <div className="zoom-controls">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 0.25}
                  title="Alejar"
                  className="zoom-btn"
                >
                  <i className="bi bi-zoom-out"></i>
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleResetZoom}
                  title="Restablecer zoom"
                  className="zoom-btn"
                >
                  <i className="bi bi-arrows-angle-contract"></i>
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 4}
                  title="Acercar"
                  className="zoom-btn"
                >
                  <i className="bi bi-zoom-in"></i>
                </Button>
              </div>
            </div>
            <div className="imagen-modal-viewport">
              <div className={`imagen-modal-container ${zoomLevel > 1 ? 'zoomed' : ''}`} align="center">
                <img
                  src={currentSrc}
                  alt="Vista ampliada"
                  className="imagen-modal"
                  style={{
                    transform: `scale(${zoomLevel})`,
                    transformOrigin: 'center center'
                  }}
                  onError={handleImageError}
                  onDoubleClick={handleImageDoubleClick}
                  draggable={false}
                  height="500px"
                  align="center"
                />
              </div>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
});

// 🚀 FUNCIÓN OPTIMIZADA PARA MARCAR COMO LEÍDO
const markMessagesAsRead = async (phoneNumber, onMessagesRead) => {
  if (!phoneNumber) return false;

  try {
    console.log(`📖 [ChatPanel] Marcando mensajes como leídos para ${phoneNumber}`);

    const response = await axios.post(`${API_BASE_URL}/markAsRead/${phoneNumber}`, {}, {
      timeout: 5000,
      headers: { 'Content-Type': 'application/json' }
    });

    if (response.data?.success) {
      console.log(`✅ [ChatPanel] Mensajes marcados como leídos exitosamente para ${phoneNumber}`);

      // Notificar al componente padre que se marcaron mensajes como leídos
      if (onMessagesRead && typeof onMessagesRead === 'function') {
        onMessagesRead(phoneNumber);
      }

      return true;
    }

    console.warn(`⚠️ [ChatPanel] Respuesta inesperada al marcar como leídos:`, response.data);
    return false;

  } catch (error) {
    console.error(`❌ [ChatPanel] Error al marcar mensajes como leídos:`, error);

    // Manejo específico de errores de autenticación (sin redirigir automáticamente)
    if (error.response?.status === 401) {
      console.error(`🔒 [ChatPanel] Error de autenticación al marcar como leídos`);
    } else if (error.response?.status === 403) {
      console.error(`🚫 [ChatPanel] Sin permisos para marcar mensajes como leídos`);
    }

    return false;
  }
};

// --- Componente Principal ChatPanel Estilo WhatsApp ---
const ChatPanel = ({ phoneNumber, onBackToList, onMessagesRead }) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [message, setMessage] = useState('');
  const [horaInicio, setHoraInicio] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [apiStatus, setApiStatus] = useState({ tested: false, connected: false });
  const [botPaused, setBotPaused] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState('whatsapp');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // 📱 ESTADOS PARA FUNCIONALIDAD MÓVIL
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  // 🚨 NUEVOS ESTADOS PARA MANEJO DE SERVIDOR CAÍDO
  const [serverDown, setServerDown] = useState(false);
  const [restartingServer, setRestartingServer] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [lastServerCheck, setLastServerCheck] = useState(null);
  // 📎 ESTADOS PARA UPLOAD DE ARCHIVOS
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [clienteNombre, setClienteNombre] = useState(null); // Estado para el nombre del cliente

  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const MIN_TEXTAREA_HEIGHT = 20;
  const MAX_TEXTAREA_HEIGHT = 120;

  const emojiButtonRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const lastMessageCountRef = useRef(0);


  const currentPhoneRef = useRef(phoneNumber);
  const isLoadingRef = useRef(false);
  const pollingTimerRef = useRef(null);
  const requestControllerRef = useRef(null);




  // Detectar cambios de tamaño de pantalla
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Efecto para probar la conexión API y reintentar
  useEffect(() => {
    let isMounted = true;
    const testApiConnection = async () => {
      try {
        // console.log('Testing API connection...');
        await axios.get('/api-test', { timeout: 5000 });
        if (isMounted) {
          setApiStatus({ tested: true, connected: true });
          setServerDown(false);
          setReconnectAttempts(0);
          setLastServerCheck(new Date());
          setError(prevError => (prevError && prevError.includes("conexión")) ? null : prevError);
        }
      } catch (e) {
        // console.error('API connection test failed:', e);
        if (isMounted) {
          setApiStatus({ tested: true, connected: false });
          setLastServerCheck(new Date());

          // 🚨 DETECTAR SI EL SERVIDOR ESTÁ COMPLETAMENTE CAÍDO
          const isServerDown = e.code === 'ECONNREFUSED' ||
            e.code === 'ENOTFOUND' ||
            e.code === 'ECONNRESET' ||
            (e.response && e.response.status >= 500) ||
            !e.response;

          if (isServerDown) {
            setServerDown(true);
            setReconnectAttempts(prev => prev + 1);
            setError('Conectando...');
          } else {
            setServerDown(false);
            setError(prevError => prevError || 'Conectando...');
          }
        }
      }
    };

    if (!apiStatus.connected) {
      testApiConnection();
    }

    const reconnectInterval = setInterval(() => {
      if (isMounted && apiStatus.tested && !apiStatus.connected) {
        // console.log('Attempting to reconnect...');
        testApiConnection();
      }
    }, serverDown ? 15000 : 30000); // Reconexión más frecuente si el servidor está caído

    return () => {
      isMounted = false;
      clearInterval(reconnectInterval);
    };
  }, [apiStatus.connected, serverDown, reconnectAttempts]); // apiStatus.tested y error quitados para simplificar el reintento

  // Función mejorada para comparar mensajes
  const areMessagesEqual = useCallback((prev, current) => {
    if (prev.length !== current.length) return false;

    return prev.every((prevMsg, index) => {
      const currentMsg = current[index];
      return prevMsg.id === currentMsg.id &&
        prevMsg.texto === currentMsg.texto &&
        prevMsg.imageUrl === currentMsg.imageUrl;
    });
  }, []);

  // 🚀 FUNCIÓN CRÍTICA CORREGIDA - CARGAR CONVERSACIÓN SIN MEZCLA
  const cargarConversacion = useCallback(async (showLoader = false, forceRefresh = false) => {
    const targetPhone = phoneNumber;
    if (!targetPhone || currentPhoneRef.current !== targetPhone) {
      if (showLoader) setCargando(false);
      return;
    }

    if (isLoadingRef.current && !showLoader) return;

    // 🚀 VERIFICAR CACHE PRIMERO
    const cacheKey = `default-${targetPhone}`;
    const cachedData = conversationCache.get(cacheKey);
    const lastFetch = lastConversationFetch;
    const now = Date.now();

    if (!forceRefresh && cachedData && lastFetch && (now - lastFetch) < CACHE_DURATION) {
      console.log(`💾 [ChatPanel] Usando datos del cache para ${targetPhone}`);
      setMensajes(cachedData);
      if (showLoader) setCargando(false);
      return;
    }

    if (showLoader) {
      setCargando(true);
      setError(null);
      isLoadingRef.current = true;
    }

    try {
      console.log(`🔍 [ChatPanel] Cargando conversación: número ${targetPhone}`);

      const url = `${API_BASE_URL}/meta/${targetPhone}`;
      const { data } = await axios.get(url, {
        timeout: 5000,
        headers: { Accept: 'application/json' }
      });

      if (currentPhoneRef.current !== targetPhone) return;

      // 🚀 EXTRAER NOMBRE DEL CLIENTE DE LA RESPUESTA
      const nombreCompleto = data?.client?.nombre_completo;
      if (nombreCompleto && nombreCompleto.trim() !== '') {
        setClienteNombre(nombreCompleto);
      } else {
        setClienteNombre(null); // Si está vacío o null, usar null para mostrar el número
      }

      if (!data?.conversation) {
        const emptyData = [];
        setMensajes(emptyData);
        conversationCache.set(cacheKey, emptyData);
        lastConversationFetch = now;
        if (showLoader) setCargando(false);
        return;
      }

      const nuevosMensajes = data.conversation.map((msg) => {
        const timestamp = msg.timestamp;
        const hora = formatDate(timestamp);
        let texto = msg.text || '';
        let imgUrl = null;
        let archivoUrl = null;
        let tipoArchivo = null;

        // Verificar si el mensaje tiene archivo multimedia
        if (msg.url_multimedia && msg.tipo_multimedia) {
          tipoArchivo = msg.tipo_multimedia;

          if (msg.url_multimedia.startsWith('http')) {
            // Ya está en el formato correcto la url
            archivoUrl = msg.url_multimedia;
          } else if (msg.url_multimedia.startsWith('/uploads/messages/')) {
            // Construir URL del archivo para servir desde el endpoint correcto
            // Extraer solo el nombre del archivo
            const filename = msg.url_multimedia.replace('/uploads/messages/', '');
            archivoUrl = `/api/mensajes/archivos/${filename}`;
            console.log(`📁 [ChatPanel] Construyendo URL desde /uploads/messages/: ${archivoUrl}`);
          } else if (msg.url_multimedia.startsWith('/uploads/')) {
            // Para compatibilidad con rutas anteriores
            const filename = msg.url_multimedia.replace('/uploads/', '');
            archivoUrl = `/api/mensajes/archivos/${filename}`;
            console.log(`📁 [ChatPanel] Construyendo URL desde /uploads/: ${archivoUrl}`);
          } else if (msg.url_multimedia.startsWith('/api/mensajes/archivos/')) {
            // Ya está en el formato correcto
            archivoUrl = msg.url_multimedia;
            console.log(`📁 [ChatPanel] URL ya correcta: ${archivoUrl}`);
          } else {
            // Asumir que es solo el nombre del archivo
            archivoUrl = `/api/mensajes/archivos/${msg.url_multimedia}`;
            console.log(`📁 [ChatPanel] Construyendo URL desde nombre: ${archivoUrl}`);
          }

          // Para imágenes, también establecer imageUrl para compatibilidad
          if (tipoArchivo === 'imagen') {
            imgUrl = archivoUrl;
          }

          // Mejorar el texto del mensaje cuando hay archivos
          if (texto.startsWith('Archivo:')) {
            // Obtener el nombre original del archivo desde metadata si está disponible
            try {
              const metadata = JSON.parse(msg.metadata_adicional || '{}');
              const nombreArchivo = metadata.nombre_archivo_original || archivoUrl.split('/').pop();

              // Seleccionar emoji según tipo de archivo
              let emoji = '📎';
              if (tipoArchivo === 'imagen') emoji = '🖼️';
              else if (tipoArchivo === 'video') emoji = '🎥';
              else if (tipoArchivo === 'audio') emoji = '🎵';
              else if (tipoArchivo === 'documento') {
                const extension = nombreArchivo.split('.').pop().toLowerCase();
                if (extension === 'pdf') emoji = '📄';
                else if (['doc', 'docx'].includes(extension)) emoji = '📝';
                else if (['xls', 'xlsx'].includes(extension)) emoji = '📊';
                else if (['ppt', 'pptx'].includes(extension)) emoji = '📽️';
                else emoji = '📄';
              }

              texto = `${emoji} ${nombreArchivo}`;
            } catch (e) {
              // Si no se puede parsear metadata, usar el texto original
              texto = texto.replace('Archivo:', '📎');
            }
          }
        } else {
          // Método de fallback: extraer filename del texto (compatibilidad)
          const imgFilename = extractUploadFilename(texto);
          if (imgFilename) {
            // Si imgFilename ya incluye 'uploads/', usarlo directamente
            if (imgFilename.startsWith('uploads/')) {
              imgUrl = `/${imgFilename}`;
            } else {
              imgUrl = `${UPLOADS_HOST}/${imgFilename}`;
            }
          }
          if (msg.imageUrl) {
            imgUrl = msg.imageUrl.startsWith('http') ? msg.imageUrl : `${UPLOADS_HOST}/${msg.imageUrl}`;
          }
        }

        return {
          id: msg.id,
          autor: msg.sender === 'user' ? 'cliente' : 'bot',
          sender: msg.sender, // Guardar el sender original para mostrar la etiqueta
          texto,
          hora,
          timestamp,
          imageUrl: imgUrl,
          archivoUrl: archivoUrl,
          tipoArchivo: tipoArchivo,
          estadoMensaje: msg.estado_mensaje || 'enviado' // Por defecto 'enviado' si no viene
        };
      });

      if (currentPhoneRef.current !== targetPhone) return;

      // 🚀 ACTUALIZAR CACHE Y ESTADO CON ORDENAMIENTO
      const sortedMensajes = sortMessages(nuevosMensajes);
      conversationCache.set(cacheKey, sortedMensajes);
      lastConversationFetch = now;
      setMensajes(sortedMensajes);
      lastMessageCountRef.current = nuevosMensajes.length;

      // 🚀 MARCAR MENSAJES COMO LEÍDOS AUTOMÁTICAMENTE
      if (nuevosMensajes.length > 0) {
        markMessagesAsRead(targetPhone, onMessagesRead);
      }

    } catch (err) {
      if (currentPhoneRef.current !== targetPhone) return;

      if (showLoader) {
        if (err.code === 'ECONNABORTED') {
          setError('Procesando solicitud...');
        } else if (err.response) {
          setError('Procesando solicitud...');
        } else {
          setError('Cargando conversación...');
        }
      }
    } finally {
      if (showLoader) {
        setCargando(false);
        isLoadingRef.current = false;
      }
    }
  }, [phoneNumber]);

  // 🤖 OBTENER ESTADO DEL BOT SIN MEZCLA
  const fetchBotStatus = useCallback(async () => {
    const targetPhone = phoneNumber;
    if (!targetPhone || currentPhoneRef.current !== targetPhone) return;

    try {
      const { data } = await axios.get(`/bot-status/${targetPhone}`, {
        timeout: 3000
      });

      if (currentPhoneRef.current !== targetPhone) return;

      if (data && typeof data.botPaused === 'boolean') {
        setBotPaused(prevState => {
          if (prevState !== data.botPaused) {
            console.log(`✅ [ChatPanel] Estado del bot: ${data.botPaused ? 'pausado' : 'activo'}`);
            return data.botPaused;
          }
          return prevState;
        });
      }
    } catch (e) {
      if (currentPhoneRef.current !== targetPhone) return;
      console.error(`❌ [ChatPanel] Error al obtener estado del bot:`, e);
    }
  }, [phoneNumber]);

  // 🚀 EFECTO PRINCIPAL CORREGIDO - CAMBIO DE NÚMERO SIN MEZCLA
  useEffect(() => {
    console.log(`📞 [ChatPanel] Cambio de número: ${currentPhoneRef.current} → ${phoneNumber}`);

    // 🔥 CANCELAR TODO LO ANTERIOR INMEDIATAMENTE
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    if (requestControllerRef.current) {
      requestControllerRef.current.abort();
      requestControllerRef.current = null;
    }
    if (globalRequestCanceller) {
      globalRequestCanceller.abort();
    }
    globalRequestCanceller = new AbortController();

    // 🚨 ACTUALIZAR REFERENCIA DEL NÚMERO ACTUAL
    currentPhoneRef.current = phoneNumber;
    isLoadingRef.current = false;

    if (!phoneNumber) {
      // 🧹 LIMPIEZA COMPLETA PARA NÚMERO VACÍO
      setMensajes([]);
      setHoraInicio('');
      setError(null);
      setBotPaused(false);
      setCargando(false);
      setMessage('');
      setShowEmojiPicker(false);
      setClienteNombre(null);
      lastMessageCountRef.current = 0;
      return;
    }

    // 🚀 LIMPIEZA Y CARGA SECUENCIAL PARA NUEVO NÚMERO
    setMensajes([]); // Limpiar inmediatamente para evitar mezcla
    setError(null);
    setCargando(true);
    setHoraInicio(formatDate(Date.now()));
    setMessage('');
    setShowEmojiPicker(false);
    setClienteNombre(null); // Limpiar nombre del cliente al cambiar de número
    lastMessageCountRef.current = 0;

    // 🔄 CARGAR CONVERSACIÓN Y ESTADO DEL BOT SECUENCIALMENTE
    const loadChatData = async () => {
      try {
        // Solo proceder si el número sigue siendo el mismo
        if (currentPhoneRef.current !== phoneNumber) return;

        await cargarConversacion(true, false);

        // Verificar nuevamente antes del estado del bot
        if (currentPhoneRef.current !== phoneNumber) return;

        // Delay mínimo para evitar sobrecarga
        setTimeout(() => {
          if (currentPhoneRef.current === phoneNumber) {
            fetchBotStatus();
          }
        }, 500);
      } catch (error) {
        if (currentPhoneRef.current === phoneNumber) {
          console.error("Error al cargar datos del chat:", error);
        }
      }
    };

    loadChatData();

    // 🧹 CLEANUP AL DESMONTAR O CAMBIAR
    return () => {
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
      if (requestControllerRef.current) {
        requestControllerRef.current.abort();
        requestControllerRef.current = null;
      }
      isLoadingRef.current = false;
    };

  }, [phoneNumber, cargarConversacion, fetchBotStatus]);

  // 🔄 POLLING OPTIMIZADO SIN MEZCLA DE CHATS
  useEffect(() => {
    if (!phoneNumber || cargando) return;

    let inFlight = false;
    let statusCheckCounter = 0;
    const POLLING_INTERVAL = 8000; // Reducido a 8 segundos para mayor velocidad

    const loop = async () => {
      // 🚨 VERIFICACIONES CRÍTICAS ANTES DE PROCEDER
      if (inFlight || !phoneNumber || currentPhoneRef.current !== phoneNumber) return;

      inFlight = true;
      try {
        // 🚨 VERIFICAR QUE SIGUE SIENDO EL NÚMERO CORRECTO
        if (currentPhoneRef.current !== phoneNumber) return;

        if (apiStatus.connected || !apiStatus.tested) {
          await cargarConversacion(false, false);
          statusCheckCounter++;

          // 🚨 VERIFICAR NUEVAMENTE ANTES DEL ESTADO DEL BOT
          if (currentPhoneRef.current !== phoneNumber) return;

          // Solo verificar estado del bot cada 20 iteraciones (100 segundos) para evitar parpadeo
          if (statusCheckCounter >= 4) {
            await fetchBotStatus();
            statusCheckCounter = 0;
          }
        }
      } catch (e) {
        // Solo registrar errores si sigue siendo el número correcto
        if (currentPhoneRef.current === phoneNumber) {
          console.error("Error en polling loop:", e);
        }
      } finally {
        inFlight = false;
      }

      // 🚨 PROGRAMAR SIGUIENTE ITERACIÓN SOLO SI EL NÚMERO NO CAMBIÓ
      if (currentPhoneRef.current === phoneNumber) {
        pollingTimerRef.current = setTimeout(loop, POLLING_INTERVAL);
      }
    };

    // 🚀 INICIAR POLLING CON DELAY INICIAL
    pollingTimerRef.current = setTimeout(loop, POLLING_INTERVAL);

    return () => {
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
      inFlight = false;
    };
  }, [phoneNumber, apiStatus.connected, apiStatus.tested, cargarConversacion, fetchBotStatus, cargando]);

  // 📅 FUNCIÓN PARA PROCESAR MENSAJES CON SEPARADORES DE FECHA
  const processMessagesWithDates = useCallback((messages) => {
    if (!messages || messages.length === 0) return [];

    const processedMessages = [];

    messages.forEach((message, index) => {
      // 📅 USAR TIMESTAMP PARA COMPARACIONES DE FECHA
      const currentMessageDate = message.timestamp || message.hora;
      const previousMessage = messages[index - 1];
      const previousMessageDate = previousMessage?.timestamp || previousMessage?.hora;

      // Si es el primer mensaje o es de un día diferente al anterior, agregar separador
      if (index === 0 || !isSameDay(currentMessageDate, previousMessageDate)) {
        processedMessages.push({
          type: 'date-separator',
          id: `date-${message.id}`,
          date: currentMessageDate
        });
      }

      // Agregar el mensaje
      processedMessages.push({
        ...message,
        type: 'message'
      });
    });

    return processedMessages;
  }, []);

  // Memorizar mensajes procesados con fechas para evitar re-renders innecesarios
  const memoizedMensajes = useMemo(() =>
    processMessagesWithDates(mensajes),
    [mensajes, processMessagesWithDates]
  );

  // 📱 SCROLL AUTOMÁTICO AL FINAL AL CARGAR CONVERSACIÓN
  useEffect(() => {
    const chatContainer = chatMessagesRef.current;
    if (!chatContainer || cargando || memoizedMensajes.length === 0) return;

    // 🚀 SIEMPRE IR AL FINAL AL CARGAR UNA CONVERSACIÓN
    setTimeout(() => {
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    }, 100); // Pequeño delay para asegurar que el DOM se haya actualizado

  }, [phoneNumber, memoizedMensajes.length, cargando]);

  // 📱 MANEJAR SCROLL Y BOTÓN DE SCROLL TO BOTTOM
  useEffect(() => {
    const chatContainer = chatMessagesRef.current;
    if (!chatContainer) return;

    const handleScroll = () => {
      if (!isMobile) return;

      const { scrollTop, scrollHeight, clientHeight } = chatContainer;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollToBottom(!isNearBottom && memoizedMensajes.length > 3);
    };

    chatContainer.addEventListener('scroll', handleScroll);

    return () => {
      chatContainer.removeEventListener('scroll', handleScroll);
    };
  }, [memoizedMensajes, isMobile]);

  // 📱 FUNCIÓN PARA SCROLL TO BOTTOM
  const scrollToBottom = useCallback(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTo({
        top: chatMessagesRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  // 🚀 FUNCIÓN PARA IR AL FINAL INMEDIATAMENTE (SIN ANIMACIÓN)
  const scrollToBottomInstant = useCallback(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, []);

  // 🚀 EMOJI HANDLERS OPTIMIZADOS
  const handleEmojiClick = useCallback(({ emoji }) => {
    setMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // 🚀 BOT ACTIONS OPTIMIZADAS
  const handleBotAction = useCallback(async (actionType) => {
    if (!phoneNumber) {
      setError("Seleccione una conversación.");
      return;
    }

    if (serverDown && actionType !== 'resume') {
      setError("Conectando...");
      return;
    }

    if (handleBotAction.isRunning) return;
    handleBotAction.isRunning = true;

    const endpoint = actionType === 'resume' ? `/resume-bot/${phoneNumber}` : `/pause-bot/${phoneNumber}`;
    const successState = actionType === 'resume' ? false : true;
    const actionText = actionType === 'resume' ? 'reanudar' : 'pausar';

    try {
      console.log(`🤖 [ChatPanel] ${actionText} bot para ${phoneNumber}`);

      // Obtener el token del localStorage si no se proporciona como parámetro
      const authToken = localStorage.getItem('token') || '';

      if (!authToken) {
        setError('No hay datos de autenticación. Por favor, inicia sesión nuevamente.');
        return;
      }

      const { data } = await axios.post(endpoint, {}, {
        timeout: 5000,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (data && data.success) {
        setBotPaused(successState);
        setError(null);
        console.log(`✅ [ChatPanel] Bot ${actionText}do exitosamente`);

        if (actionType === 'resume' && serverDown) {
          setServerDown(false);
        }

        setTimeout(() => fetchBotStatus(), 200);
      } else {
        const errorMsg = data?.error || data?.message || 'Respuesta inválida del servidor';
        setError('Procesando...');
      }
    } catch (e) {
      console.error(`❌ [ChatPanel] Error al ${actionText} el bot:`, e);

      if (e.response) {
        const status = e.response.status;
        const errorData = e.response.data;

        if (status === 400) {
          setError('Procesando solicitud...');
        } else if (status >= 500) {
          setError('Procesando solicitud...');
          setServerDown(true);
        } else {
          setError('Procesando solicitud...');
        }
      } else {
        const isServerDown = e.code === 'ECONNREFUSED' ||
          e.code === 'ENOTFOUND' ||
          e.code === 'ECONNRESET' ||
          e.code === 'ETIMEDOUT';

        if (isServerDown) {
          setServerDown(true);
          setError('Conectando...');
        } else {
          setError('Conectando...');
        }
      }
    } finally {
      handleBotAction.isRunning = false;
    }
  }, [phoneNumber, serverDown, fetchBotStatus]);

  const handleResumeBot = useCallback(() => handleBotAction('resume'), [handleBotAction]);
  const handlePauseBot = useCallback(() => {
    if (serverDown) {
      setError("Conectando...");
      return;
    }
    handleBotAction('pause');
  }, [handleBotAction, serverDown]);

  // 🚀 ENVÍO DE MENSAJES OPTIMIZADO - Meta API
  const handleEnviar = useCallback(async () => {
    if (!message.trim() || !phoneNumber) {
      return;
    }

    const cleanMessage = message.replace(/\/uploads\/([\w-]+\.(?:png|jpe?g|gif|webp))(?:\s|$)/i, '$1');
    const tempId = `temp_${Date.now()}`;
    const now = Date.now();

    // ✅ UI UPDATE INMEDIATO (Optimistic UI)
    setMensajes(prev => sortMessages([...prev, {
      id: tempId,
      autor: 'bot',
      texto: cleanMessage,
      hora: formatDate(now),
      timestamp: now,
      imageUrl: null,
      estadoMensaje: 'enviado' // Estado inicial cuando se envía
    }]));

    setTimeout(() => scrollToBottomInstant(), 10);

    // ✅ LIMPIAR FORMULARIO
    setMessage('');
    if (textareaRef.current) {
      textareaRef.current.style.height = `${MIN_TEXTAREA_HEIGHT}px`;
      textareaRef.current.style.overflowY = 'hidden';
      if (isMobile) {
        setTimeout(() => textareaRef.current?.focus(), 50);
      }
    }
    setShowEmojiPicker(false);

    try {
      console.log(`📤 [ChatPanel] Enviando mensaje a ${phoneNumber} vía Meta API`);

      // 🚀 LLAMADA AL BACKEND - authInterceptor maneja automáticamente el token
      const requestData = {
        contenido: cleanMessage,
        numero_telefono: phoneNumber
      };

      const response = await axios.post('/api/mensajes/enviar', requestData, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 12000 // Timeout para Meta API
      });

      // ✅ VALIDACIÓN DE RESPUESTA API
      if (!response.data || !response.data.success) {
        throw new Error(response.data?.message || 'Error del servidor al enviar mensaje');
      }

      console.log(`✅ [ChatPanel] Mensaje enviado exitosamente vía Meta API`);
      console.log(`📊 [ChatPanel] WhatsApp ID:`, response.data.data?.mensaje?.wamid_msg);

      setError(null);

      // 🔄 INVALIDAR CACHE Y REFRESCAR CONVERSACIÓN
      const cacheKey = `default-${phoneNumber}`;
      conversationCache.delete(cacheKey);
      lastConversationFetch = Date.now();

      // Refrescar después de un delay para permitir que Meta API procese
      setTimeout(() => {
        cargarConversacion(false, true);
      }, 1000);

    } catch (error) {
      console.error(`❌ [ChatPanel] Error enviando mensaje:`, error);

      // 🚨 MANEJO ESPECÍFICO DE ERRORES
      let errorMessage = 'Error enviando mensaje';

      if (error.response) {
        const status = error.response.status;
        const serverMessage = error.response.data?.message || error.response.data?.error;

        switch (status) {
          case 401:
            errorMessage = 'Sesión expirada. Por favor, inicia sesión nuevamente';
            setTimeout(() => {
              window.location.href = '/login';
            }, 2000);
            break;
          case 403:
            errorMessage = 'No tienes permisos para enviar mensajes';
            break;
          case 429:
            errorMessage = 'Demasiadas solicitudes. Espera un momento';
            break;
          case 500:
            errorMessage = serverMessage || 'Error interno del servidor';
            break;
          default:
            errorMessage = serverMessage || `Error del servidor (${status})`;
        }
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Tiempo de espera agotado. Intenta nuevamente';
      } else if (error.code === 'ECONNREFUSED' || !error.response) {
        errorMessage = 'Servidor no disponible. Verifica tu conexión';
        setServerDown(true);
      } else {
        errorMessage = error.message || 'Error de conexión';
      }

      setError(errorMessage);

      // 🔄 REVERTIR UI (Quitar mensaje temporal)
      setMensajes(prev => prev.filter(m => m.id !== tempId));

      // 📱 LIMPIAR ERROR DESPUÉS DE 5 SEGUNDOS
      setTimeout(() => {
        setError(null);
      }, 5000);
    }
  }, [message, phoneNumber, isMobile, scrollToBottomInstant, cargarConversacion]);

  // 📎 FUNCIONES PARA MANEJO DE ARCHIVOS
  const handleFileSelect = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFileChange = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tamaño del archivo (50MB máximo)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      setError('El archivo es demasiado grande. Máximo 50MB permitido.');
      return;
    }

    // Validar tipos de archivo permitidos
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'audio/mpeg', 'audio/wav', 'audio/ogg',
      'video/mp4', 'video/avi', 'video/mov'
    ];

    if (!allowedTypes.includes(file.type)) {
      setError('Tipo de archivo no permitido. Solo se permiten imágenes, PDFs, documentos, audio y video.');
      return;
    }

    // Solo seleccionar el archivo, no enviarlo automáticamente
    setSelectedFile(file);
    setError(null);
  }, []);

  // Nueva función para cancelar selección de archivo
  const handleCancelFile = useCallback(() => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Nueva función para confirmar envío de archivo
  const handleConfirmSendFile = useCallback(async () => {
    if (selectedFile) {
      await handleSendFile(selectedFile);
    }
  }, [selectedFile]);

  const handleSendFile = useCallback(async (file) => {
    if (!file || !phoneNumber) {
      return;
    }

    setUploadingFile(true);
    setError(null);

    const tempId = `temp_file_${Date.now()}`;
    const now = Date.now();

    // Mostrar mensaje temporal en la UI
    const fileTypeIcon = file.type.startsWith('image/') ? '🖼️' :
      file.type === 'application/pdf' ? '📄' :
        file.type.startsWith('audio/') ? '🎵' :
          file.type.startsWith('video/') ? '🎥' : '📎';

    setMensajes(prev => sortMessages([...prev, {
      id: tempId,
      autor: 'bot',
      texto: `${fileTypeIcon} Enviando archivo: ${file.name}...`,
      hora: formatDate(now),
      timestamp: now,
      imageUrl: null,
      estadoMensaje: 'enviado' // Estado inicial cuando se envía
    }]));

    setTimeout(() => scrollToBottomInstant(), 10);

    try {
      console.log(`📎 [ChatPanel] Enviando archivo`);

      const formData = new FormData();
      formData.append('archivo', file);
      formData.append('numero_telefono', phoneNumber);
      formData.append('codigo_persona', phoneNumber); // Usar número como código de persona por ahora
      formData.append('contenido', `Archivo: ${file.name}`);

      // 🚀 ENVÍO DE ARCHIVO - authInterceptor maneja automáticamente el token
      const response = await axios.post('/api/mensajes/enviar-archivo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000 // 30 segundos para archivos grandes
      });

      // ✅ VALIDACIÓN DE RESPUESTA
      if (!response.data || !response.data.success) {
        throw new Error(response.data?.error || response.data?.message || 'Error del servidor al enviar archivo');
      }

      console.log(`✅ [ChatPanel] Archivo enviado exitosamente vía Meta API`);
      console.log(`📎 [ChatPanel] Respuesta:`, response.data);

      // Mantener estado del bot si está disponible
      if (response.data.botPaused !== undefined) {
        setBotPaused(response.data.botPaused);
      }

      setError(null);

      // 🔄 INVALIDAR CACHE Y REFRESCAR CONVERSACIÓN
      const cacheKey = `default-${phoneNumber}`;
      conversationCache.delete(cacheKey);
      lastConversationFetch = Date.now();

      // Refrescar después de enviar archivo (más tiempo para procesar)
      setTimeout(() => {
        cargarConversacion(false, true);
      }, 1200);

    } catch (error) {
      console.error(`❌ [ChatPanel] Error enviando archivo:`, error);

      // 🚨 MANEJO ESPECÍFICO DE ERRORES PARA ARCHIVOS
      let errorMessage = 'Error enviando archivo';

      if (error.response) {
        const status = error.response.status;
        const serverMessage = error.response.data?.message || error.response.data?.error;

        switch (status) {
          case 401:
            errorMessage = 'Sesión expirada. Inicia sesión nuevamente';
            setTimeout(() => {
              window.location.href = '/login';
            }, 2000);
            break;
          case 403:
            errorMessage = 'No tienes permisos para enviar archivos';
            break;
          case 413:
            errorMessage = 'Archivo demasiado grande. Máximo 16MB';
            break;
          case 415:
            errorMessage = 'Tipo de archivo no permitido';
            break;
          case 429:
            errorMessage = 'Demasiadas solicitudes. Espera un momento';
            break;
          case 500:
            errorMessage = serverMessage || 'Error interno del servidor';
            break;
          default:
            errorMessage = serverMessage || `Error del servidor (${status})`;
        }
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Tiempo de espera agotado. Archivo muy grande o conexión lenta';
      } else if (error.code === 'ECONNREFUSED' || !error.response) {
        errorMessage = 'Servidor no disponible. Verifica tu conexión';
        setServerDown(true);
      } else {
        errorMessage = error.message || 'Error de conexión al enviar archivo';
      }

      setError(errorMessage);

      // 🔄 REVERTIR UI (Quitar mensaje temporal)
      setMensajes(prev => prev.filter(m => m.id !== tempId));

      // 📱 LIMPIAR ERROR DESPUÉS DE 5 SEGUNDOS
      setTimeout(() => {
        setError(null);
      }, 5000);
    } finally {
      setUploadingFile(false);
      setSelectedFile(null);
      // Limpiar el input file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [phoneNumber, scrollToBottomInstant, cargarConversacion]);

  // 🚀 TEXTAREA EFFECTS OPTIMIZADOS
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      let newHeight = textareaRef.current.scrollHeight;

      if (newHeight > MAX_TEXTAREA_HEIGHT) {
        newHeight = MAX_TEXTAREA_HEIGHT;
        textareaRef.current.style.overflowY = 'auto';
      } else {
        textareaRef.current.style.overflowY = 'hidden';
      }

      textareaRef.current.style.height = `${Math.max(MIN_TEXTAREA_HEIGHT, newHeight)}px`;
    }
  }, [message]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEnviar();
    }
  }, [handleEnviar]);

  // 🚀 RECONNECT OPTIMIZADO
  const handleReconnect = useCallback(async () => {
    if (!phoneNumber) {
      setError("Seleccione una conversación.");
      return;
    }

    setError('Conectando...');
    setCargando(true);

    try {
      await axios.get('/api-test', { timeout: 5000 });

      setServerDown(false);
      setError(null);

      // Limpiar cache y recargar
      conversationCache.clear();
      lastConversationFetch = 0;

      await cargarConversacion(true, true);
      await fetchBotStatus();

    } catch (err) {
      const isServerDown = err.code === 'ECONNREFUSED' ||
        err.code === 'ENOTFOUND' ||
        err.code === 'ECONNRESET' ||
        !err.response;

      if (isServerDown) {
        setServerDown(true);
        setError('Conectando...');
      } else {
        setError('Conectando...');
      }
    } finally {
      setCargando(false);
    }
  }, [phoneNumber, cargarConversacion, fetchBotStatus]);

  // 🚀 RENDER OPTIMIZADO - EARLY RETURNS
  if (!phoneNumber) {
    return (
      <div className="whatsapp-chat-container">
        {isMobile ? (
          <div className="whatsapp-chat-header">
            <div className="header-content">
              {onBackToList && (
                <Button
                  variant="link"
                  className="back-btn"
                  onClick={onBackToList}
                  title="Volver a contactos"
                >
                  <i className="bi bi-arrow-left"></i>
                </Button>
              )}
              <div className="contact-info">
                <div className="contact-details">
                  <h4 className="contact-name">Selecciona un contacto</h4>
                  <div className="contact-status">
                    <span className="status-dot offline"></span>
                    Sin seleccionar
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="whatsapp-chat-placeholder">
            <div className="placeholder-content">
              <div className="placeholder-icon">
                <i className="bi bi-chat-dots-fill"></i>
              </div>
              <h3 className="placeholder-title">WhatsApp</h3>
              <p className="placeholder-subtitle">
                Selecciona una conversación para comenzar a chatear
              </p>
            </div>
          </div>
        )}

        {isMobile && (
          <>
            <div className="whatsapp-messages-area">
              <div className="empty-messages">
                <div className="empty-content">
                  <i className="bi bi-chat-text-fill"></i>
                  <p>Selecciona un contacto</p>
                  <small>para comenzar a chatear</small>
                </div>
              </div>
            </div>

            <div className="whatsapp-input-area">
              <div className="input-container">
                <div className="text-input-container">
                  <Form.Control
                    as="textarea"
                    placeholder="Selecciona un contacto para chatear"
                    className="message-input"
                    disabled={true}
                    rows={1}
                    style={{ display: 'block' }}
                  />
                </div>
                <Button className="send-btn" disabled={true}>
                  <i className="bi bi-send-fill"></i>
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="whatsapp-chat-container">
      {/* Header optimizado */}
      <div className="whatsapp-chat-header">
        <div className="header-content">
          {isMobile && onBackToList && (
            <Button
              variant="link"
              className="back-btn"
              onClick={onBackToList}
              title="Volver a contactos"
            >
              <i className="bi bi-arrow-left"></i>
            </Button>
          )}

          <div className="contact-info">
            <div className="contact-avatar">
              <i className="bi bi-person-circle"></i>
            </div>
            <div className="contact-details">
              <h4 className="contact-name">{clienteNombre || phoneNumber}</h4>
              <div className="contact-status">
                {cargando ? (
                  <span className="status-loading">
                    <i className="spinner-border spinner-border-sm me-1" role="status"></i>
                    cargando...
                  </span>
                ) : (
                  <>
                    {phoneNumber && (
                      <span style={{ fontSize: '0.9em', marginRight: '8px', display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
                        <i className="bi bi-telephone" style={{ fontSize: '0.85em' }}></i>
                        {phoneNumber}
                      </span>
                    )}
                    <span className={`status-dot ${!serverDown ? 'online' : 'offline'}`}></span>
                    {!serverDown ? 'conectado' : 'desconectado'}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="header-actions">
            <div className="bot-status-indicator">
              <Badge
                bg={botPaused ? 'warning' : 'success'}
                className="bot-badge"
              >
                {botPaused ? '⏸️' : '🤖'}
              </Badge>
            </div>

            <div className="dropdown">
              {/* <Button
                variant="link"
                className="header-menu-btn"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="bi bi-three-dots-vertical"></i>
              </Button> */}
              <ul className="dropdown-menu dropdown-menu-end">
                <li>
                  <button
                    className="dropdown-item"
                    onClick={botPaused ? handleResumeBot : handlePauseBot}
                    disabled={!phoneNumber || cargando}
                  >
                    <i className={`bi ${botPaused ? 'bi-play-circle' : 'bi-pause-circle'} me-2`}></i>
                    {botPaused ? 'Reanudar Bot' : 'Pausar Bot'}
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={handleReconnect} disabled={cargando}>
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Reconectar
                  </button>
                </li>
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <div className="dropdown-item-text">
                    <small>Canal: {selectedChannel}</small>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {serverDown && (
          <div className="connection-alert">
            <div className="loading-spinner">
              <i className="spinner-border text-muted" role="status"></i>
            </div>
            <span className="ms-2">Conectando...</span>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={handleReconnect}
              className="ms-2"
              disabled={cargando}
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Reintentar
            </Button>
          </div>
        )}
      </div>

      {/* Área de mensajes optimizada */}
      <div className="whatsapp-messages-area" ref={chatMessagesRef}>
        {cargando && memoizedMensajes.length === 0 && (
          <div className="loading-messages">
            <div className="loading-spinner">
              <i className="spinner-border text-muted" role="status"></i>
            </div>
            <p>Cargando conversación...</p>
          </div>
        )}

        {error && !cargando && (
          <div className="loading-messages">
            <div className="loading-spinner">
              <i className="spinner-border text-muted" role="status"></i>
            </div>
            <p>Procesando...</p>
          </div>
        )}

        {!cargando && memoizedMensajes.length === 0 && !error && (
          <div className="empty-messages">
            <div className="empty-content">
              <i className="bi bi-chat-text-fill"></i>
              <p>No hay mensajes aquí todavía...</p>
              <small>Los mensajes aparecerán aquí</small>
            </div>
          </div>
        )}

        {memoizedMensajes.map((item) => {
          if (item.type === 'date-separator') {
            return <DateSeparator key={item.id} date={item.date} />;
          } else {
            return <Mensaje key={item.id} msg={item} />;
          }
        })}

        {isMobile && showScrollToBottom && (
          <button
            className="scroll-to-bottom-btn"
            onClick={scrollToBottom}
            title="Ir al final"
          >
            <i className="bi bi-chevron-down"></i>
          </button>
        )}
      </div>

      {/* Controles móviles */}
      {isMobile && phoneNumber && (
        <div className={`mobile-bot-controls ${serverDown ? 'server-down' : ''}`}>
          <div className="bot-controls">
            {botPaused || serverDown ? (
              <Button
                onClick={handleResumeBot}
                variant="success"
                disabled={!phoneNumber || cargando}
                className="mobile-bot-btn"
                title={serverDown ? "Reconectar" : "Reanudar Bot"}
              >
                <i className="bi bi-play-circle"></i>
                {serverDown ? 'Reconectar' : 'Reanudar'}
              </Button>
            ) : (
              <Button
                onClick={handlePauseBot}
                variant="warning"
                disabled={!phoneNumber || cargando || serverDown}
                className="mobile-bot-btn"
                title="Pausar Bot"
              >
                <i className="bi bi-pause-circle"></i>
                Pausar
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Preview de archivo seleccionado */}
      {selectedFile && (
        <div className="file-preview-container">
          <div className="file-preview">
            <div className="file-info">
              <div className="file-icon">
                {selectedFile.type.startsWith('image/') ? (
                  <img
                    src={URL.createObjectURL(selectedFile)}
                    alt="Preview"
                    className="file-thumbnail"
                  />
                ) : selectedFile.type === 'application/pdf' ? (
                  <i className="bi bi-file-earmark-pdf file-type-icon"></i>
                ) : selectedFile.type.startsWith('audio/') ? (
                  <i className="bi bi-file-earmark-music file-type-icon"></i>
                ) : selectedFile.type.startsWith('video/') ? (
                  <i className="bi bi-file-earmark-play file-type-icon"></i>
                ) : (
                  <i className="bi bi-file-earmark file-type-icon"></i>
                )}
              </div>
              <div className="file-details">
                <span className="file-name">{selectedFile.name}</span>
                <span className="file-size">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</span>
              </div>
            </div>
            <div className="file-actions">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleCancelFile}
                className="cancel-file-btn"
                title="Cancelar"
              >
                <i className="bi bi-x"></i>
              </Button>
              <Button
                variant="success"
                size="sm"
                onClick={handleConfirmSendFile}
                disabled={uploadingFile}
                className="send-file-btn"
                title="Enviar archivo"
              >
                {uploadingFile ? (
                  <i className="bi bi-arrow-clockwise spin"></i>
                ) : (
                  <i className="bi bi-send-fill"></i>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Área de input optimizada */}
      <div className="whatsapp-input-area">
        <div className="input-container">
          <Button
            variant="link"
            className="attach-btn"
            onClick={handleFileSelect}
            disabled={!phoneNumber || cargando || serverDown || uploadingFile}
            title={serverDown ? "Servidor no disponible" : uploadingFile ? "Enviando archivo..." : "Adjuntar archivo"}
          >
            {uploadingFile ? (
              <i className="bi bi-arrow-clockwise spin"></i>
            ) : (
              <i className="bi bi-paperclip"></i>
            )}
          </Button>

          {/* Input file oculto */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,audio/*,video/*"
          />

          <div className="text-input-container">
            <Form.Control
              as="textarea"
              ref={textareaRef}
              placeholder={
                serverDown ? "Servidor no disponible" :
                  phoneNumber ? "Escribe un mensaje" : "Selecciona un contacto"
              }
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              className="message-input"
              disabled={!phoneNumber || cargando || serverDown}
              style={{ display: 'block' }}
              rows={1}
            />

            <Button
              variant="link"
              className="emoji-btn"
              onClick={() => phoneNumber && !serverDown && setShowEmojiPicker(!showEmojiPicker)}
              disabled={!phoneNumber || cargando || serverDown}
              ref={emojiButtonRef}
              title={serverDown ? "Servidor no disponible" : "Emojis"}
            >
              <i className="bi bi-emoji-smile"></i>
            </Button>

            {showEmojiPicker && phoneNumber && !serverDown && (
              <div className="emoji-picker-popup" ref={emojiPickerRef}>
                <EmojiPicker
                  onEmojiClick={handleEmojiClick}
                  height={350}
                  width="100%"
                  searchDisabled={false}
                  lazyLoadEmojis
                  previewConfig={{ showPreview: false }}
                  skinTonesDisabled
                />
              </div>
            )}
          </div>

          {message.trim() ? (
            <Button
              className="send-btn"
              onClick={handleEnviar}
              disabled={!phoneNumber || !message.trim() || cargando || serverDown}
              title={serverDown ? "Servidor no disponible" : "Enviar mensaje"}
            >
              <i className="bi bi-send-fill"></i>
            </Button>
          ) : (
            !isMobile && (
              null
              /* <Button
                variant="link"
                className="mic-btn"
                disabled={!phoneNumber || cargando || serverDown}
                title={serverDown ? "Servidor no disponible" : "Mensaje de voz"}
              >
                <i className="bi bi-mic-fill"></i>
              </Button> */
            )
          )}
        </div>

        {!isMobile && (
          <div className="desktop-controls">
            <div className={`bot-controls ${serverDown ? 'server-down' : ''}`}>
              {botPaused || serverDown ? (
                <Button
                  onClick={handleResumeBot}
                  size="sm"
                  variant="success"
                  disabled={!phoneNumber || cargando}
                  title={serverDown ? "Reconectar" : "Reanudar Bot"}
                >
                  <i className="bi bi-play-circle me-1"></i>
                  {serverDown ? 'Reconectar' : 'Reanudar Bot'}
                </Button>
              ) : (
                <Button
                  onClick={handlePauseBot}
                  size="sm"
                  variant="warning"
                  disabled={!phoneNumber || cargando || serverDown}
                >
                  <i className="bi bi-pause-circle me-1"></i>
                  Pausar Bot
                </Button>
              )}
            </div>

            <div className="channel-selector">
              <Form.Select
                size="sm"
                value={selectedChannel}
                onChange={(e) => setSelectedChannel(e.target.value)}
                disabled={!phoneNumber || cargando}
                className="channel-select"
              >
                <option value="whatsapp">WhatsApp</option>
                {/* <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option> */}
              </Form.Select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(ChatPanel);